import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, Users } from 'lucide-react';
import { API } from '../config/api';

// Cache of live user IDs — refreshed every 15s
let liveUserCache = {};
let lastFetched = 0;
let fetchPromise = null;

async function refreshLiveUsers() {
  const now = Date.now();
  if (now - lastFetched < 15000 && Object.keys(liveUserCache).length >= 0 && lastFetched > 0) {
    return liveUserCache;
  }
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(`${API}/live-system/live-creators`)
    .then(r => r.ok ? r.json() : { live_user_ids: {} })
    .then(d => {
      liveUserCache = d.live_user_ids || {};
      lastFetched = Date.now();
      fetchPromise = null;
      return liveUserCache;
    })
    .catch(() => {
      fetchPromise = null;
      return liveUserCache;
    });
  return fetchPromise;
}

/**
 * LiveBadge — Shows a red LIVE indicator on any user card/profile.
 * Usage: <LiveBadge userId="user_xxx" size="sm" />
 */
export function LiveBadge({ userId, size = 'sm', showViewers = false, className = '' }) {
  const navigate = useNavigate();
  const [isLive, setIsLive] = useState(false);
  const [streamId, setStreamId] = useState(null);

  useEffect(() => {
    let mounted = true;
    refreshLiveUsers().then(cache => {
      if (mounted && cache[userId]) {
        setIsLive(true);
        setStreamId(cache[userId]);
      } else if (mounted) {
        setIsLive(false);
        setStreamId(null);
      }
    });

    const interval = setInterval(() => {
      refreshLiveUsers().then(cache => {
        if (!mounted) return;
        if (cache[userId]) {
          setIsLive(true);
          setStreamId(cache[userId]);
        } else {
          setIsLive(false);
          setStreamId(null);
        }
      });
    }, 15000);

    return () => { mounted = false; clearInterval(interval); };
  }, [userId]);

  if (!isLive) return null;

  const sizeClasses = {
    xs: 'text-[8px] px-1 py-0 gap-0.5',
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-0.5 gap-1',
    lg: 'text-sm px-2.5 py-1 gap-1.5',
  };

  const iconSize = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5',
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (streamId) navigate(`/watch/${streamId}`);
      }}
      className={`inline-flex items-center rounded-full bg-red-500 text-white font-bold animate-pulse cursor-pointer hover:bg-red-600 transition-colors ${sizeClasses[size] || sizeClasses.sm} ${className}`}
      data-testid={`live-badge-${userId}`}
      title="Watch Live"
    >
      <Radio className={iconSize[size] || iconSize.sm} />
      LIVE
    </button>
  );
}

/**
 * LiveDot — Minimal red dot indicator for tight spaces (avatars, thumbnails).
 */
export function LiveDot({ userId, className = '' }) {
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let mounted = true;
    refreshLiveUsers().then(cache => {
      if (mounted) setIsLive(!!cache[userId]);
    });
    const interval = setInterval(() => {
      refreshLiveUsers().then(cache => {
        if (mounted) setIsLive(!!cache[userId]);
      });
    }, 15000);
    return () => { mounted = false; clearInterval(interval); };
  }, [userId]);

  if (!isLive) return null;

  return (
    <span className={`absolute block w-3 h-3 rounded-full bg-red-500 ring-2 ring-background animate-pulse ${className}`}
      data-testid={`live-dot-${userId}`} />
  );
}

/**
 * useLiveUsers — Hook to get live user ID set. Refreshes every 15s.
 */
export function useLiveUsers() {
  const [liveUsers, setLiveUsers] = useState({});

  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      refreshLiveUsers().then(cache => {
        if (mounted) setLiveUsers({ ...cache });
      });
    };
    refresh();
    const interval = setInterval(refresh, 15000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  return liveUsers;
}

/**
 * LiveNowBanner — Horizontal scrollable strip of live creators.
 * Drop into any page: <LiveNowBanner />
 */
export function LiveNowBanner({ maxItems = 10 }) {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/live-system/live-now?limit=${maxItems}`)
      .then(r => r.ok ? r.json() : { streams: [] })
      .then(d => { if (mounted) setStreams(d.streams || []); })
      .catch(() => {});

    const interval = setInterval(() => {
      fetch(`${API}/live-system/live-now?limit=${maxItems}`)
        .then(r => r.ok ? r.json() : { streams: [] })
        .then(d => { if (mounted) setStreams(d.streams || []); })
        .catch(() => {});
    }, 30000);

    return () => { mounted = false; clearInterval(interval); };
  }, [maxItems]);

  if (streams.length === 0) return null;

  return (
    <div className="bg-red-950/20 border-y border-red-500/20 py-3 px-4" data-testid="live-now-banner">
      <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 shrink-0 pr-3 border-r border-red-500/30">
          <Radio className="w-4 h-4 text-red-400 animate-pulse" />
          <span className="text-xs font-bold text-red-400 uppercase tracking-wider whitespace-nowrap">Live Now</span>
        </div>
        {streams.map(s => (
          <button
            key={s.stream_id}
            onClick={() => navigate(`/watch/${s.stream_id}`)}
            className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-full px-3 py-1.5 shrink-0 transition-colors"
            data-testid={`live-stream-${s.stream_id}`}
          >
            {s.host_avatar ? (
              <img src={s.host_avatar} alt="" className="w-6 h-6 rounded-full object-cover ring-2 ring-red-500" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-red-500/30 ring-2 ring-red-500 flex items-center justify-center">
                <Radio className="w-3 h-3 text-red-400" />
              </div>
            )}
            <div className="text-left">
              <p className="text-xs font-semibold text-foreground truncate max-w-[120px]">{s.host_name}</p>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Users className="w-2.5 h-2.5" /> {s.viewer_count || 0}
                {s.uptime && <span className="ml-1">{s.uptime}</span>}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export { refreshLiveUsers };
